import usePersonnelDetails from "@/hooks/usePersonnelDetails";
import { faCheckCircle, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Link, Skeleton} from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, useEffect, useRef, useState } from "react";
import useIsAdminSection from "@/hooks/useIsAdminSection";
import useDebouncedValue from "@/hooks/useDebouncedValue";

interface IStaffCheckProps {
    speakerSection?: boolean;
}

const StaffCheck: FC<IStaffCheckProps> = ({
    speakerSection
}) => {
    const {state: {value: firstName}} = useScopedField<string>("firstName", {value: true});
    const {state: {value: lastName}} = useScopedField<string>("lastName", {value: true});
    
    const {state: {value: staffConfirmed}, context: {setValue: setStaffConfirmed}} = useScopedField<boolean>("staffConfirmed", {value: true});
    
    const {state : {value: staff}, context: {setValue: setStaff}} = useScopedField<boolean>("staff", { value: true });
    const {state: {value: alreadyRegistered}, context: {setValue: setAlreadyRegistered}} = useScopedField<boolean>("alreadyRegistered", {value: true});

    const debouncedFirstName = useDebouncedValue(firstName);
    const debouncedLastName = useDebouncedValue(lastName);
    const { isStaff, registered, loading } = usePersonnelDetails(debouncedFirstName ?? undefined, debouncedLastName ?? undefined);

    const isAdminSection = useIsAdminSection();

    const [deferredLoading, setDeferredLoading] = useState(false);
    const timerRef = useRef<number>();

    useEffect(() => {
        if (loading) {
            timerRef.current = window.setTimeout(() => {
                setDeferredLoading(true);
            }, 250);
        }
        else {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current)
                timerRef.current = undefined;
            }

            setDeferredLoading(false);
        }
    }, [loading, setDeferredLoading, timerRef])

    useEffect(() => {
        setStaffConfirmed(isStaff, true, true, true, false);
        setStaff(current => {
            if (!isStaff && current) {
                return false;
            }

            if (isStaff && !current) {
                return true;
            }

            return current;
        }, true, true, true, false)
    }, [isStaff, setStaff, setStaffConfirmed])

    useEffect(() => {
        setAlreadyRegistered(registered);

    }, [registered, setAlreadyRegistered])

    if (deferredLoading && loading) {
        return (
            <Alert
                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                severity="info"
                sx={{
                    alignItems: 'center'
                }}
            >
                <AlertTitle>Staff Status &ndash; <Skeleton width={120} sx={{display: 'inline-block'}} /></AlertTitle>
                Checking Staff status...
            </Alert>
        )
    }

    if (alreadyRegistered && !isAdminSection) {
        return null;
    }

    if (firstName && lastName) {
        return (
            <>
            {
                !staffConfirmed ? 
                    isAdminSection ?
                        !staff ? 
                            <Alert
                                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                                severity="info"
                                sx={{
                                    alignItems: 'center'
                                }}
                            >
                                <AlertTitle>Staff Status &ndash; Not-Staff</AlertTitle>
                                {
                                    speakerSection ?
                                        <>
                                            Staff not applied.
                                        </>
                                    :
                                        <>
                                            Staff price not applied.
                                        </>
                                }
                            </Alert>
                        :
                        <Alert
                            icon={<FontAwesomeIcon icon={faCheckCircle} />}
                            severity="success"
                            sx={{
                                alignItems: 'center'
                            }}
                        >
                            <AlertTitle>Staff Status &ndash; Staff</AlertTitle>
                            
                            {
                                speakerSection ?
                                    <>
                                        You are a Staff member
                                    </>
                                :
                                    <>
                                        You are a Staff member - Free registation
                                    </>
                            }
                        </Alert>

                    :
                        <Alert
                            icon={<FontAwesomeIcon icon={faInfoCircle} />}
                            severity="info"
                            sx={{
                                alignItems: 'center'
                            }}
                        >
                            <AlertTitle>Staff Status &ndash; Not-Staff</AlertTitle>
                            {
                                speakerSection ?
                                    <>
                                        You are not BCT staff.
                                    </>
                                :
                                    <>
                                        You are not BCT staff. If this is incorrect, please contact BCT on <Link href="tel:0249255255">02 4925 5255</Link>.
                                    </>
                            }
                        </Alert>
                :
                    <Alert
                        icon={<FontAwesomeIcon icon={faCheckCircle} />}
                        severity="success"
                        sx={{
                            alignItems: 'center'
                        }}
                    >
                        <AlertTitle>Staff Status &ndash; Staff</AlertTitle>
                            {
                                speakerSection ?
                                    <>
                                        You are a Staff member
                                    </>
                                :
                                    <>
                                        You are a Staff member - Free registation
                                    </>
                            }
                    </Alert>
            }
            </>
        )
    }

    return (
        <Alert
            icon={<FontAwesomeIcon icon={faInfoCircle} />}
            severity="info"
            sx={{
                alignItems: 'center'
            }}
        >
                <AlertTitle>Staff Status &ndash; Unknown</AlertTitle>
                Please enter your first and last name.
        </Alert>
    );
}

export default StaffCheck;